import { MultiLineChartLine } from '@design/charts/multi-line-chart/MultiLineChart.types';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { AnalyticsLineChart } from '@terminal/modules/analytics-line-chart/AnalyticsLineChart';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import { t } from 'i18next';
import { useEffect, useMemo } from 'react';

import { usePerformanceFiltersData } from '../hooks/usePerformanceFiltersData';
import { usePerformanceStore } from '../hooks/usePerformanceStore';

const MAX_BREAKDOWN_COMPANIES = 10;

const formatData = (tsData?: { value: number; index: string }[]) =>
  tsData?.map(d => ({
    value: d.value,
    date: new Date(d.index),
  })) ?? [];

export const PerformanceChart = () => {
  const { colors } = useChartTheme();
  const {
    showBenchmark,
    showBreakdown,
    setBreakdownDisabled,
    setDisplayedEntityTags,
  } = usePerformanceStore(state => ({
    showBenchmark: state.showBenchmark,
    showBreakdown: state.showBreakdown,
    setBreakdownDisabled: state.setBreakdownDisabled,
    setDisplayedEntityTags: state.setDisplayedEntityTags,
  }));
  const { entityTags, themeData, spxData, entitiesData, isFetching, isError } =
    usePerformanceFiltersData();

  const aggregate = useMemo(
    () => formatData(themeData?.aggregate),
    [themeData?.aggregate]
  );

  const benchmark = useMemo(
    () => formatData(spxData?.aggregate),
    [spxData?.aggregate]
  );

  const breakdown = useMemo(() => {
    const multiColors = colors.getMulti(
      Object.entries(themeData?.entities ?? {}).length
    );

    return Object.entries(themeData?.entities ?? {}).map(([tag, data], i) => {
      const entity = entitiesData.find(e => e.tag === tag);
      return {
        label: entity?.name_short || entity?.name || tag,
        data: formatData(data),
        color: multiColors[i].value,
      };
    });
  }, [themeData?.entities, entitiesData, colors]);

  const multiLineConfig: Record<string, MultiLineChartLine[]> = useMemo(
    () => ({
      aggregate: [
        {
          label: t('widget:themeOverview.themePerformance'),
          color: 'var(--icon-default)',
          data: aggregate,
        },
      ],
      benchmark: [
        {
          label: t('widget:themeOverview.benchmark', {
            name: 'SPX',
          }),
          color: 'var(--icon-soft)',
          data: benchmark,
          isBenchmark: true,
        },
      ],
      breakdown,
    }),
    [aggregate, benchmark, breakdown]
  );

  useEffect(() => {
    setBreakdownDisabled(entityTags.length > MAX_BREAKDOWN_COMPANIES);
    setDisplayedEntityTags(entityTags);
  }, [entityTags]);

  if (isFetching) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '340px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (isError) {
    throw new Error('Chart data fetching error');
  }

  return (
    <AnalyticsLineChart
      multiLineConfig={multiLineConfig}
      showBenchmark={showBenchmark}
      showBreakdown={showBreakdown}
    />
  );
};
